import { createReducer, on } from '@ngrx/store';

// Interfaces
import { ICaptionsState } from '@interfaces/call.interfaces';

// Actions
import {
  setSessionParams,
  setDevicesState,
  setWorkerState,
  clearWorkerState,
  changeDevicesState,
  toggleMirrorState,
  toggleChatState,
  setNotificationMessageState,
  setCaptionsStatusFromSocket,
  toggleCaptionsStatus,
  toggleCaptionsSuccessStatus,
  toggleCaptionsErrorStatus,
  clearCaptionsStatusState,
} from './session.actions';

export const sessionFeatureKey = 'session';

export interface ISessionStateData {
  sessionId: string;
  callId: string;
  isMirror: boolean;
  isChatOn: boolean;
  isNotificationMessage: boolean;
  isRecordCall: boolean;
}

export interface IWorkerStateData {
  call_duration: number;
  call_id: number;
  call_rate: number;
  sender_first_name: string;
  sender_id: number;
  sender_last_name: string;
  sender_photo: string;
  session_id: string;
}

export interface IDevicesStateData {
  audioDeviceId: string;
  audioDeviceLabel: string;
  videoDeviceId: string;
  videoDeviceLabel: string;
  audio: boolean;
  video: boolean;
}

export interface ISessionState {
  session: ISessionStateData | null;
  worker: IWorkerStateData | null;
  devices: IDevicesStateData | null;
  captions: ICaptionsState;
}

export const initialState: ISessionState = {
  session: null,
  worker: null,
  devices: null,
  captions: {
    status: {
      is_worker_captions_enabled: false,
      is_user_captions_enabled: false,
      worker_language: 'en',
      user_language: 'en',
    },
    loading: false,
    error: null,
  },
};

export const sessionReducer = createReducer(
  initialState,

  // Session params
  on(setSessionParams, (state, { session }) => ({
    ...state,
    session,
  })),

  // Devices
  on(setDevicesState, (state, { devices }) => ({
    ...state,
    devices,
  })),
  on(changeDevicesState, (state, { devices }) => ({
    ...state,
    devices: state.devices
      ? {
          ...state.devices,
          ...devices,
        }
      : state.devices,
  })),

  // Worker
  on(setWorkerState, (state, { worker }) => ({
    ...state,
    worker,
  })),
  on(clearWorkerState, state => ({
    ...state,
    worker: null,
  })),

  // Mirror
  on(toggleMirrorState, (state, { mirror }) => {
    if (!state.session) {
      return state;
    }

    const isMirror = typeof mirror === 'boolean' ? mirror : !state.session.isMirror;

    return {
      ...state,
      session: {
        ...state.session,
        isMirror,
      },
    };
  }),

  // Chat
  on(toggleChatState, state => {
    if (!state.session) {
      return state;
    }

    return {
      ...state,
      session: {
        ...state.session,
        isChatOn: !state.session.isChatOn,
        isNotificationMessage: false,
      },
    };
  }),
  on(setNotificationMessageState, (state, { state: notificationState }) => {
    if (!state.session) {
      return state;
    }

    return {
      ...state,
      session: {
        ...state.session,
        isNotificationMessage: notificationState,
      },
    };
  }),

  // Captions
  on(setCaptionsStatusFromSocket, (state, { data }) => ({
    ...state,
    captions: {
      status: data,
      loading: false,
      error: null,
    },
  })),
  on(toggleCaptionsStatus, state => ({
    ...state,
    captions: {
      ...state.captions,
      loading: true,
      error: null,
    },
  })),
  on(toggleCaptionsSuccessStatus, (state, { captions }) => ({
    ...state,
    captions: {
      status: {
        ...state.captions.status,
        is_user_captions_enabled: captions,
      },
      loading: false,
      error: null,
    },
  })),
  on(toggleCaptionsErrorStatus, (state, { error }) => ({
    ...state,
    captions: {
      ...state.captions,
      loading: false,
      error: error ?? null,
    },
  })),
  on(clearCaptionsStatusState, state => ({
    ...state,
    captions: {
      status: {
        is_worker_captions_enabled: false,
        is_user_captions_enabled: false,
        worker_language: 'en',
        user_language: 'en',
      },
      loading: false,
      error: null,
    },
  })),
);
